<script>
  import OutlinedInput from '@/components/form/OutlinedInput.vue';

  export default {
    name: 'PasswordInput',

    components: { OutlinedInput },

    emits: ['input', 'validation-changed'],

    props: {
      value: {
        type: String,
        default: '',
      },
      label: {
        type: String,
        default: 'Password',
      },
    },

    data: () => ({
      password: '',
      isValid: false,

      passwordStrength: {
        0: 'very weak',
        1: 'weak',
        2: 'medium',
        3: 'strong',
      },

      validationTexts: {
        notEmpty: 'Password cannot be empty',
        upper: '1 upper case letter',
        len: '8 or more characters',
        special: '1 special character',
      },

      showPassword: false,
    }),

    created() {
      this.password = this.value;
    },

    watch: {
      password(newVal) {
        this.$emit('input', newVal);
      },
    },

    computed: {
      inputType() {
        return this.showPassword ? 'text' : 'password';
      },

      passwordRules() {
        return [(v) => this.isStrongPassword(v) || 'Please choose a stronger password'];
      },

      passwordScore() {
        const password = this.password;
        const score = {
          len: 0,
          upper: 0,
          special: 0,
        };

        if (password.length >= 8) {
          score.len = 1;
        }

        if (/[A-Z]/.test(password)) {
          score.upper = 1;
        }

        if (/[!-/:-@[-`{-~]/.test(password)) {
          score.special = 1;
        }

        return score;
      },
      forbiddenSymbols() {
        const forbiddenSymbols = [
          {
            pattern: /\s/,
            matched: false,
            text: 'whitespace',
          },
        ];

        forbiddenSymbols.forEach((value) => {
          value.matched = value.pattern.test(this.password);
        });

        if (forbiddenSymbols.some((symbol) => symbol.matched)) {
          return `Password cannot contain: ${forbiddenSymbols
            .filter((symbol) => symbol.matched)
            .map((symbol) => symbol.text)
            .join(',')}`;
        }
        return '';
      },
      passwordRulesPassed() {
        return Object.values(this.passwordScore).reduce((acc, val) => acc + val, 0);
      },

      passwordStrengthClass() {
        switch (this.passwordRulesPassed) {
          case 0:
            return 'red-text';
          case 1:
            return 'yellow-text';
          case 2:
            return 'orange-text';
          case 3:
            return 'green-text';
          default:
            return '';
        }
      },
    },

    methods: {
      isStrongPassword() {
        this.isValid = Object.values(this.passwordScore).every((score) => score === 1) && !this.forbiddenSymbols;
        this.$emit('validation-changed', this.isValid);

        return this.isValid;
      },

      getLiClass(index) {
        if (index <= this.passwordRulesPassed) {
          switch (this.passwordRulesPassed) {
            case 0:
              return 'red';
            case 1:
              return 'weak';
            case 2:
              return 'medium';
            case 3:
              return 'strong';
            default:
              return 'red';
          }
        }

        return 'neutral';
      },
    },
  };
</script>

<template>
  <div>
    <outlined-input
      v-model="password"
      :rules="passwordRules"
      :type="inputType"
      :label="label"
      hide-details
      data-test-id="password-input"
    >
      <template #append>
        <v-btn icon @click="showPassword = !showPassword" tabindex="-1" data-test-id="password-eye-toggle">
          <v-icon>{{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
        </v-btn>
      </template>
    </outlined-input>
    <div style="height: 100px" class="white--text w-full font-size-12">
      <ul class="progress-bar">
        <li :class="getLiClass(0)"></li>
        <li v-for="index in Object.keys(passwordScore).length" :key="index" :class="getLiClass(index)"></li>
      </ul>

      <div class="w-full d-flex justify-space-between">
        <span>Password must contain at least:</span>
        <span :class="passwordStrengthClass" data-test-id="password-strength">
          {{ passwordStrength[passwordRulesPassed] }}
        </span>
      </div>

      <div v-for="rule in Object.keys(passwordScore)" :key="rule" :data-test-id="'password-criteria-' + rule">
        <v-icon class="font-size-18 green-text" v-if="passwordScore[rule]">mdi-check</v-icon>
        <v-icon class="font-size-18 red-text" v-else>mdi-close</v-icon>
        <span class="ml-1">{{ validationTexts[rule] }}</span>
      </div>
      <div>
        <v-icon class="font-size-18 red-text" v-if="forbiddenSymbols">mdi-close</v-icon>
        <span class="ml-1">{{ forbiddenSymbols }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  $green: #00ff5e;
  $red: #ff0000;
  $yellow: #face08;
  $orange: #ff7300;

  .green-text {
    color: $green;
  }

  .red-text {
    color: $red;
  }

  .yellow-text {
    color: $yellow;
  }

  .orange-text {
    color: $orange;
  }

  ul.progress-bar {
    display: flex;
    gap: 4px;
    padding: 0;
    margin: 10px 0;

    li {
      width: 100%;
      display: block;
      height: 4px;
      border-radius: 2px;
      background-color: rgba(255, 255, 255, 0.2);

      &.red {
        background-color: $red !important;
      }

      &.weak {
        background-color: $yellow;
      }

      &.medium {
        background-color: $orange;
      }

      &.strong {
        background-color: $green;
      }
    }
  }
</style>
